import React, { useState, useRef, useEffect } from "react";
import { Avatar } from "../../../../../ui/components/Avatar";
import { Button } from "../../../../../ui/components/Button";
import { ToggleGroup } from "../../../../../ui/components/ToggleGroup";
import * as SubframeCore from "@subframe/core";
import { FeatherChevronDown, FeatherInfo } from "@subframe/core";
import { Tooltip } from "../../../../../ui/components/Tooltip";
import { useExpenseBreakdown } from "../../hooks/useExpenseBreakdown";
import { formatPlAmount } from "../../utils/formatPlAmount";
import {
  getExpenseBreakdownDateRangeSubtext,
  getExpenseBreakdownUnavailableUserMessage,
} from "../../utils/expenseBreakdownDateRange";
import type {
  ExpenseBreakdownTimeframe,
  ExpenseBreakdownType,
} from "../../../domain/entities/ExpenseBreakdownEntity";

export interface ExpenseBreakdownRowItem {
  id?: string;
  name: string;
  amount: string;
  percent: string;
  avatarLetter: string;
  avatarVariant?: "brand" | "success" | "warning" | "error" | "neutral";
  barPercent: number;
}

const AVATAR_VARIANTS: Array<
  "brand" | "success" | "warning" | "error" | "neutral"
> = ["brand", "success", "warning", "error", "neutral"];

function toAvatarLetter(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "—";
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase().slice(0, 2);
  }
  return trimmed.slice(0, 2).toUpperCase();
}

function entityItemsToRows(
  items: { name: string; amount: number; percent: number }[],
): ExpenseBreakdownRowItem[] {
  return items.map((item, i) => ({
    name: item.name,
    amount: formatPlAmount(item.amount),
    percent: `${item.percent}%`,
    avatarLetter: toAvatarLetter(item.name),
    avatarVariant: AVATAR_VARIANTS[i % AVATAR_VARIANTS.length],
    barPercent: Math.min(100, Math.max(0, item.percent)),
  }));
}

export interface ExpenseBreakdownSectionProps {
  title?: string;
}

export function ExpenseBreakdownSection({
  title = "Expense Breakdown",
}: ExpenseBreakdownSectionProps) {
  const {
    breakdownType,
    setBreakdownType,
    breakdownTimeframe,
    setBreakdownTimeframe,
    items,
    periodLabel,
    summary,
    loading,
    error,
    dateRangeStart,
    dateRangeEnd,
    breakdownUnavailableReason,
  } = useExpenseBreakdown("vendor");

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const dateRangeSubtext = getExpenseBreakdownDateRangeSubtext(
    breakdownTimeframe,
    {
      dateRangeStart,
      dateRangeEnd,
      breakdownUnavailableReason,
    },
  );
  const unavailableUserMessage = getExpenseBreakdownUnavailableUserMessage(
    breakdownUnavailableReason,
  );
  const listCaption =
    breakdownType === "vendor" ? "Expense by Vendor" : "Expense by Category";
  const buttonLabel = breakdownType === "vendor" ? "By Vendor" : "By Category";
  const rows = entityItemsToRows(items);

  const summaryTotal = summary?.totalExpense ?? null;
  const summaryBills = summary?.billsNetExpense ?? null;
  const summaryJournals = summary?.journalsExpense ?? null;

  const invoicesExpenseGl = summary?.invoicesExpenseGl ?? null;
  const bankVendorExpense = summary?.bankVendorExpense ?? null;
  const bankCharges = summary?.bankCharges ?? null;
  const bankForexExpenseAdj = summary?.bankForexExpenseAdj ?? null;
  const summaryOthers =
    invoicesExpenseGl == null ||
    bankVendorExpense == null ||
    bankCharges == null ||
    bankForexExpenseAdj == null
      ? null
      : -invoicesExpenseGl +
        bankVendorExpense +
        bankCharges +
        bankForexExpenseAdj;

  const handleSelectType = (type: ExpenseBreakdownType) => {
    setBreakdownType(type);
    setDropdownOpen(false);
  };
  const handleSelectTimeframe = (value: string) => {
    if (value === "ytd" || value === "last_month") {
      setBreakdownTimeframe(value as ExpenseBreakdownTimeframe);
    }
  };

  const totalsGridClassName = "grid w-full grid-cols-2 gap-3 md:grid-cols-4";
  const totalCardClassName =
    "flex h-full min-h-[76px] flex-col justify-between gap-2 rounded-lg border border-neutral-200 bg-white px-4 py-3 shadow-sm ring-1 ring-black/5";
  const totalLabelClassName =
    "text-caption font-caption text-subtext-color leading-tight";
  const totalValueClassName =
    "text-heading-3 font-heading-3 text-default-font tabular-nums";

  return (
    <div className="flex w-full flex-col items-start gap-6 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
      <div className="flex w-full items-center justify-between gap-4">
        <div className="flex min-w-0 flex-col items-start gap-1">
          <span className="text-heading-3 font-heading-3 text-default-font">
            {title}
          </span>
          <span className="text-body font-body text-subtext-color">
            {periodLabel} · {dateRangeSubtext}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <ToggleGroup
            value={breakdownTimeframe}
            onValueChange={handleSelectTimeframe}
          >
            <ToggleGroup.Item icon={null} value="last_month">
              Last month
            </ToggleGroup.Item>
            <ToggleGroup.Item
              className={
                breakdownTimeframe === "ytd" ? "bg-brand-100" : undefined
              }
              icon={null}
              value="ytd"
            >
              FY
            </ToggleGroup.Item>
          </ToggleGroup>
          <div className="relative" ref={dropdownRef}>
            <Button
              variant="neutral-secondary"
              iconRight={<FeatherChevronDown />}
              onClick={() => setDropdownOpen((o) => !o)}
            >
              {buttonLabel}
            </Button>
            {dropdownOpen && (
              <div className="absolute right-0 top-full z-10 mt-1 min-w-[140px] rounded-md border border-neutral-200 bg-white py-1 shadow-lg">
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-body font-body text-default-font hover:bg-neutral-50"
                  onClick={() => handleSelectType("vendor")}
                >
                  By Vendor
                </button>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-body font-body text-default-font hover:bg-neutral-50"
                  onClick={() => handleSelectType("category")}
                >
                  By Category
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex w-full flex-col gap-3">
        <span className="text-caption font-caption text-neutral-500 uppercase tracking-widest">
          Totals — {dateRangeSubtext}
        </span>
        {loading ? (
          <div className={totalsGridClassName}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-[76px] w-full animate-pulse rounded-lg border border-neutral-200 bg-white shadow-sm ring-1 ring-black/5"
              />
            ))}
          </div>
        ) : (
          <div className={totalsGridClassName}>
            <div className={totalCardClassName}>
              <span className={totalLabelClassName}>Total expense</span>
              <span className={totalValueClassName}>
                {summaryTotal == null ? "—" : formatPlAmount(summaryTotal)}
              </span>
            </div>
            <div className={totalCardClassName}>
              <span className={totalLabelClassName}>
                Bills (incl. credit/debit notes)
              </span>
              <span className={totalValueClassName}>
                {summaryBills == null ? "—" : formatPlAmount(summaryBills)}
              </span>
            </div>
            <div className={totalCardClassName}>
              <span className={totalLabelClassName}>Journals</span>
              <span className={totalValueClassName}>
                {summaryJournals == null
                  ? "—"
                  : formatPlAmount(summaryJournals)}
              </span>
            </div>
            <div className={totalCardClassName}>
              <div className="flex items-center gap-2">
                <span className={totalLabelClassName}>Others</span>
                <SubframeCore.Tooltip.Provider>
                  <SubframeCore.Tooltip.Root>
                    <SubframeCore.Tooltip.Trigger asChild={true}>
                      <span className="cursor-help mt-0.5 inline-flex items-center">
                        <FeatherInfo className="h-3.5 w-3.5 text-neutral-500" />
                      </span>
                    </SubframeCore.Tooltip.Trigger>
                    <SubframeCore.Tooltip.Portal>
                      <SubframeCore.Tooltip.Content
                        side="bottom"
                        align="start"
                        sideOffset={6}
                        asChild={true}
                      >
                        <Tooltip className="max-w-[360px] whitespace-pre-wrap">
                          {`Included in Others:\n` +
                            `- Reimbursement From Customers: ${
                              invoicesExpenseGl == null
                                ? "—"
                                : formatPlAmount(invoicesExpenseGl)
                            } (subtracted)\n` +
                            `- No Bill + Bank Charges and Fees: ${
                              bankVendorExpense == null
                                ? "—"
                                : formatPlAmount(bankVendorExpense)
                            }\n` +
                            `- Foreign customer receipt related expense (Bank Charges): ${
                              bankCharges == null
                                ? "—"
                                : formatPlAmount(bankCharges)
                            }\n` +
                            `- Exchange Gain or Loss: ${
                              bankForexExpenseAdj == null
                                ? "—"
                                : formatPlAmount(bankForexExpenseAdj)
                            }`}
                        </Tooltip>
                      </SubframeCore.Tooltip.Content>
                    </SubframeCore.Tooltip.Portal>
                  </SubframeCore.Tooltip.Root>
                </SubframeCore.Tooltip.Provider>
              </div>
              <span className={totalValueClassName}>
                {summaryOthers == null ? "—" : formatPlAmount(summaryOthers)}
              </span>
            </div>
          </div>
        )}
      </div>

      <span className="text-caption font-caption text-subtext-color">
        {listCaption}
      </span>

      {error && (
        <p className="text-caption font-caption text-red-600" role="alert">
          {error}
        </p>
      )}
      {loading ? (
        <div className="flex w-full flex-col gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex w-full items-center gap-4">
              <div className="h-10 w-10 flex-none animate-pulse rounded-full bg-neutral-200" />
              <div className="flex flex-1 flex-col gap-2">
                <div className="h-4 w-32 animate-pulse rounded bg-neutral-200" />
                <div className="h-2 w-full animate-pulse rounded-full bg-neutral-100" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex w-full flex-col items-start gap-4">
          {rows.length === 0 ? (
            <p className="text-body font-body text-subtext-color">
              {unavailableUserMessage ?? "No expense data for this period."}
            </p>
          ) : (
            rows.map((row, i) => (
              <div key={row.id ?? i} className="flex w-full items-center gap-4">
                <Avatar
                  variant={row.avatarVariant ?? "brand"}
                  size="medium"
                  image=""
                >
                  {row.avatarLetter}
                </Avatar>
                <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
                  <div className="flex w-full items-center justify-between">
                    <span className="text-body-bold font-body-bold text-default-font">
                      {row.name}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-body-bold font-body-bold text-default-font">
                        {row.amount}
                      </span>
                      <span className="text-body font-body text-subtext-color">
                        {row.percent}
                      </span>
                    </div>
                  </div>
                  <div className="flex h-2 w-full flex-none flex-col items-start rounded-full bg-neutral-100">
                    <div
                      className="flex h-2 flex-none flex-col items-start rounded-full bg-blue-500"
                      style={{ width: `${row.barPercent}%` }}
                    />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
